import { AxiosError, AxiosRequestConfig } from 'axios'

/**
 * Extended Axios config to support retry tracking
 */
export interface AxiosConfigWithRetry extends AxiosRequestConfig {
	_retryAttempt?: number
}

/**
 * Minimal retry helper for BFF HTTP client
 * Only retries GET requests with 500 errors and network timeouts
 */
export class RetryHelper {
	/**
	 * Conservative retry condition: Only retry safe, idempotent operations
	 * - GET requests with 500 errors (likely cache misses, safe to retry)
	 * - GET requests with network timeouts (idempotent, safe to retry)
	 */
	static shouldRetry(error: AxiosError): boolean {
		const config = error.config as AxiosConfigWithRetry
		const method = config?.method?.toUpperCase()

		return method === 'GET' &&
			(error.response?.status === 500 || (!error.response && (error.code === 'ECONNABORTED' || error.message.includes('timeout'))))
	}

	/**
	 * Creates a retry interceptor for BFF Axios instances
	 * Automatically retries GET 500 errors and timeouts with exponential backoff
	 */
	static createRetryInterceptor(axiosInstance: any) {
		return async (error: AxiosError): Promise<any> => {
			const config = error.config as AxiosConfigWithRetry

			if (!config) {
				return Promise.reject(error)
			}

			config._retryAttempt = config._retryAttempt || 0
			const shouldRetry = RetryHelper.shouldRetry(error)
			const maxRetries = 2

			if (!shouldRetry || config._retryAttempt >= maxRetries) {
				return Promise.reject(error)
			}

			config._retryAttempt++

			// Calculate delay with exponential backoff (1s, 2s)
			const delay = 1000 * Math.pow(2, config._retryAttempt - 1)

			console.warn(`Request failed (attempt ${config._retryAttempt}/${maxRetries + 1}), retrying in ${delay}ms:`, error.message)

			await new Promise((resolve) => setTimeout(resolve, delay))
			return axiosInstance(config)
		}
	}
}
